import { useLocalSearchParams, useRouter } from 'expo-router'
import type React from 'react'
import { useCallback, useState } from 'react'
import { Linking, Platform, Pressable, StyleSheet, View } from 'react-native'
import { Button, Card, HelperText, Icon, Text, useTheme } from 'react-native-paper'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useAuth } from '@/lib/auth'
import { captureException, errorToString } from '@/lib/errors'
import { type Control, TextInput, useForm, z, zodResolver } from '@/ui/form'
import { Link } from '@/ui/Link'

const signInSchema = z.object({
    email: z.string().email('Please enter a valid email address').min(1, 'Email is required'),
    password: z.string().min(1, 'Password is required'),
})

type SignInFormData = z.infer<typeof signInSchema>

const useSignInSubmission = () => {
    const router = useRouter()
    const { back } = useLocalSearchParams<{ back?: string }>()

    const { login } = useAuth({ throwIfAnon: false })

    const [isLoading, setIsLoading] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const onSubmit = useCallback(
        async (data: SignInFormData) => {
            setIsLoading(true)
            setSubmitError(null)

            try {
                const { user, error } = await login(data.email, data.password)

                if (error) {
                    setSubmitError(error || 'Failed to sign in')
                    return
                }

                if (user?.primaryOrgSlug) {
                    const destination = back ? decodeURIComponent(back) : `/${user.primaryOrgSlug}`
                    router.replace(destination as '/')
                } else {
                    setSubmitError('No organization found for this account')
                }
            } catch (error) {
                captureException('Sign in', error)
                setSubmitError(errorToString(error, 'Failed to sign in'))
            } finally {
                setIsLoading(false)
            }
        },
        [router, login, back]
    )

    return { onSubmit, isLoading, submitError }
}

interface SignInFormProps {
    control: Control<SignInFormData>
    onSubmit: () => void
    isLoading: boolean
    isValid: boolean
    submitError: string | null
}

const SignInForm: React.FC<SignInFormProps> = ({
    control,
    onSubmit,
    isLoading,
    isValid,
    submitError,
}) => (
    <View style={styles.form}>
        <TextInput
            name="email"
            control={control}
            label="Email Address"
            placeholder="Enter your email"
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
        />

        <TextInput
            name="password"
            control={control}
            label="Password"
            placeholder="Enter your password"
            secureTextEntry
            autoComplete="current-password"
        />

        {submitError && (
            <HelperText type="error" style={styles.submitError}>
                {submitError}
            </HelperText>
        )}

        <Button
            mode="contained"
            onPress={onSubmit}
            disabled={isLoading || !isValid}
            loading={isLoading}
            style={styles.button}
        >
            {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>
    </View>
)

export default function SignInPage() {
    useAvoidSoftInputForScreen()
    const { onSubmit, isLoading, submitError } = useSignInSubmission()
    const theme = useTheme()

    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<SignInFormData>({
        resolver: zodResolver(signInSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    })

    const ContentWrapper = Platform.OS === 'ios' ? SafeAreaView : View

    return (
        <ContentWrapper
            style={[styles.safeContainer, { backgroundColor: theme.colors.background }]}
        >
            <View style={styles.container}>
                <View style={styles.header}>
                    <Text variant="headlineLarge">Welcome Back</Text>
                    <Text variant="bodyMedium" style={styles.subtitle}>
                        Sign in to your Job Time account
                    </Text>
                </View>

                <Card
                    style={[styles.infoCard, { backgroundColor: theme.colors.secondaryContainer }]}
                >
                    <Card.Content style={styles.infoContent}>
                        <Icon
                            source="information"
                            size={20}
                            color={theme.colors.onSecondaryContainer}
                        />
                        <Text
                            variant="bodySmall"
                            style={{ color: theme.colors.onSecondaryContainer, flex: 1 }}
                        >
                            First time here? Check your email for an invite link from your
                            organization.
                        </Text>
                    </Card.Content>
                </Card>

                <SignInForm
                    control={control}
                    onSubmit={handleSubmit(onSubmit)}
                    isLoading={isLoading}
                    isValid={isValid}
                    submitError={submitError}
                />

                <View style={styles.footer}>
                    <View style={styles.linkRow}>
                        <Link href="/auth/reset">
                            <Text variant="bodyMedium" style={{ color: theme.colors.primary }}>
                                Forgot Password?
                            </Text>
                        </Link>
                    </View>

                    <View style={styles.linkRow}>
                        <Text variant="bodyMedium">Don't have an account? </Text>
                        <Link href="/auth/sign-up">
                            <Text variant="bodyMedium" style={{ color: theme.colors.primary }}>
                                Sign Up
                            </Text>
                        </Link>
                    </View>

                    <Pressable
                        style={styles.privacyLink}
                        onPress={() => Linking.openURL('https://jobtimey.com/privacy')}
                    >
                        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                            Privacy Policy
                        </Text>
                    </Pressable>
                </View>
            </View>
        </ContentWrapper>
    )
}

const styles = StyleSheet.create({
    safeContainer: {
        flex: 1,
    },
    container: {
        flex: 1,
        padding: 16,
        maxWidth: 400,
        width: '100%',
        alignSelf: 'center',
        gap: 16,
    },
    header: {
        gap: 8,
    },
    subtitle: {
        opacity: 0.7,
    },
    infoCard: {
        marginBottom: 8,
    },
    infoContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 8,
    },
    form: {
        gap: 16,
    },
    submitError: {
        marginTop: 8,
    },
    button: {
        marginTop: 16,
    },
    footer: {
        gap: 12,
        marginTop: 24,
    },
    linkRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    privacyLink: {
        marginTop: 16,
        alignItems: 'center',
    },
})
